import { useEffect } from 'react';
import { useParams, Navigate, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const SUPPORTED_LANGS = ['ko', 'en'] as const;
type SupportedLang = (typeof SUPPORTED_LANGS)[number];

function isSupportedLang(lang: string | undefined): lang is SupportedLang {
  return SUPPORTED_LANGS.includes(lang as SupportedLang);
}

/**
 * Inner layout rendered only when lang is valid.
 * All hooks are called unconditionally here.
 *
 * NOTE: hreflang injection via useEffect is a minimal SPA approach.
 *       For SSR, replace this with a proper Head/Helmet solution.
 */
function ValidLangLayout({ lang }: { lang: SupportedLang }) {
  const { i18n } = useTranslation();

  // Sync i18next language with URL param
  useEffect(() => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  // Inject hreflang <link> tags into <head> (cleaned up on unmount / lang change)
  useEffect(() => {
    const origin = window.location.origin;
    const pathWithoutLang = window.location.pathname.replace(/^\/(ko|en)/, '') || '/';
    const search = window.location.search;
    const hash = window.location.hash;
    const suffix = `${pathWithoutLang}${search}${hash}`;

    const links: HTMLLinkElement[] = [];

    const createLink = (hreflang: string, href: string) => {
      const el = document.createElement('link');
      el.rel = 'alternate';
      el.setAttribute('hreflang', hreflang);
      el.href = href;
      document.head.appendChild(el);
      links.push(el);
    };

    createLink('ko', `${origin}/ko${suffix}`);
    createLink('en', `${origin}/en${suffix}`);
    createLink('x-default', `${origin}/ko${suffix}`);

    return () => {
      links.forEach((el) => el.parentNode?.removeChild(el));
    };
  }, [lang]);

  return <Outlet />;
}

/**
 * Route wrapper that:
 * 1. Reads the :lang URL parameter
 * 2. Redirects to /ko if lang is not 'ko' or 'en'
 * 3. Delegates to ValidLangLayout for all hook logic
 */
export function LangLayout() {
  const { lang } = useParams<{ lang: string }>();

  if (!isSupportedLang(lang)) {
    return <Navigate to="/ko" replace />;
  }

  return <ValidLangLayout lang={lang} />;
}
